import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, Briefcase, Target, Shield, DollarSign } from 'lucide-react';
import GlossaryTerm from './GlossaryTerm';

const SIZING_LABELS = {
  equal_weight: 'Equal Weight (fixed)',
  equal_weight_compound: 'Equal Weight (compound)',
  all_in: 'All-In',
  all_in_compound: 'All-In (compound)',
};

const TYPE_TONE = {
  baseline: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  positional: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  swing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2 py-1.5 border-b last:border-0 text-sm">
    <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
    <span className="text-muted-foreground text-xs uppercase tracking-wide w-28 shrink-0 pt-0.5">{label}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

/**
 * Card che descrive una strategia: filosofia + parametri formattati con glossario.
 *
 * Props:
 *  - spec: dict del JSON spec (da /api/admin/quant/strategies/{name} → field "spec")
 */
const StrategyInfoCard = ({ spec }) => {
  const [expanded, setExpanded] = useState(false);

  if (!spec || Object.keys(spec).length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6 text-sm text-muted-foreground text-center">
          Spec non disponibile per questa strategia.
        </CardContent>
      </Card>
    );
  }

  const desc = spec.description || '';
  const shortDesc = desc.length > 240 ? desc.slice(0, 240).trim() + '…' : desc;
  const longDescNeeded = desc.length > 240;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">{spec.name}</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="font-mono text-[10px]">v{spec.version}</Badge>
            {spec.type && (
              <GlossaryTerm term={spec.type}>
                <Badge className={`text-[10px] capitalize ${TYPE_TONE[spec.type] || ''}`}>{spec.type}</Badge>
              </GlossaryTerm>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Description */}
        {desc && (
          <div className="mb-3">
            <p className="text-sm leading-relaxed">
              {expanded ? desc : shortDesc}
            </p>
            {longDescNeeded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="mt-1 h-6 px-2 text-xs"
              >
                {expanded ? <><ChevronUp className="w-3 h-3 mr-1" /> Riduci</> : <><ChevronDown className="w-3 h-3 mr-1" /> Mostra tutto</>}
              </Button>
            )}
          </div>
        )}

        {/* Parametri chiave */}
        <div className="space-y-0">
          {/* Universe */}
          {spec.universe && spec.universe.length > 0 && (
            <Row icon={Briefcase} label="Universe">
              <div className="flex flex-wrap gap-1">
                {spec.universe.map((s) => (
                  <Badge key={s} variant="outline" className="font-mono text-[10px]">{s}</Badge>
                ))}
                {spec.universe_min_inception && (
                  <span className="text-[10px] text-muted-foreground ml-2 pt-0.5">
                    da {spec.universe_min_inception}
                  </span>
                )}
              </div>
            </Row>
          )}

          {/* Entry rule */}
          {spec.entry?.rule && (
            <Row icon={Target} label="Entry">
              <div className="text-xs">
                <span className="font-mono">{spec.entry.rule}</span>
                {spec.entry.params && Object.keys(spec.entry.params).length > 0 && (
                  <span className="text-muted-foreground ml-2">
                    {Object.entries(spec.entry.params).map(([k, v]) => `${k}=${v}`).join(' · ')}
                  </span>
                )}
              </div>
            </Row>
          )}

          {/* Exit rule */}
          {spec.exit?.rule && (
            <Row icon={Target} label="Exit">
              <div className="text-xs">
                <span className="font-mono">{spec.exit.rule}</span>
                {spec.exit.params && Object.keys(spec.exit.params).length > 0 && (
                  <span className="text-muted-foreground ml-2">
                    {Object.entries(spec.exit.params).map(([k, v]) => `${k}=${v}`).join(' · ')}
                  </span>
                )}
              </div>
            </Row>
          )}

          {/* Sizing */}
          {spec.sizing && (
            <Row icon={DollarSign} label="Sizing">
              <div className="text-xs flex items-center gap-2 flex-wrap">
                <GlossaryTerm term={spec.sizing.method}>
                  {SIZING_LABELS[spec.sizing.method] || spec.sizing.method}
                </GlossaryTerm>
                <span className="text-muted-foreground">
                  {(spec.sizing.max_pct_per_position * 100).toFixed(0)}% per posizione
                </span>
                {spec.sizing.max_concurrent_positions && (
                  <span className="text-muted-foreground">
                    · max {spec.sizing.max_concurrent_positions} concurrent
                  </span>
                )}
              </div>
            </Row>
          )}

          {/* Stops */}
          {spec.stops && (spec.stops.stop_loss_pct || spec.stops.stop_loss_atr_mult || spec.stops.take_profit_trailing_atr_mult) && (
            <Row icon={Shield} label="Stops">
              <div className="text-xs space-x-2">
                {spec.stops.stop_loss_pct && (
                  <span>SL fisso: <span className="font-mono">−{(spec.stops.stop_loss_pct * 100).toFixed(0)}%</span></span>
                )}
                {spec.stops.stop_loss_atr_mult && (
                  <span>· SL <GlossaryTerm term="ATR">ATR</GlossaryTerm>: <span className="font-mono">−{spec.stops.stop_loss_atr_mult}×</span></span>
                )}
                {spec.stops.take_profit_trailing_atr_mult && (
                  <span>· TP trail: <span className="font-mono">{spec.stops.take_profit_trailing_atr_mult}× ATR</span></span>
                )}
              </div>
            </Row>
          )}

          {/* Costs */}
          {spec.costs && (
            <Row icon={DollarSign} label="Costi">
              <div className="text-xs text-muted-foreground">
                {spec.costs.slippage_bps != null && <span>slippage {spec.costs.slippage_bps} bps</span>}
                {spec.costs.commission_bps != null && <span> · commission {spec.costs.commission_bps} bps</span>}
              </div>
            </Row>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StrategyInfoCard;
